import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer } from '@helios/ui/overlay';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncQuery } from '../adapters/powersync';
import { qPatientsDrawerList, type PatientDrawerRow, type DrawerTab } from '@helios/data-access';
import { NavIcon, useBrandTokens } from './shared';
import {
  usePatientTabs,
  getPatientColor,
  getInitialsShort,
  getShortLabel,
} from './PatientTabsContext';

interface OpenPatientDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ─── Tab config ───

const TABS: Array<{ key: DrawerTab; label: string }> = [
  { key: 'all', label: 'All Patients' },
  { key: 'favorites', label: 'Favorites' },
  { key: 'today', label: "Today's Patients" },
  { key: 'new', label: 'New Patients' },
  { key: 'recent', label: 'Recently Visited' },
];

// ─── Helpers ───

function computeAge(birthDate: string | null): number | null {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const monthDiff = now.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function useDebouncedValue(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ─── Component ───

export function OpenPatientDrawer({ open, onOpenChange }: OpenPatientDrawerProps) {
  const brandTokens = useBrandTokens();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.uid ?? '';
  const { openPatientTab } = usePatientTabs();

  const [activeTab, setActiveTab] = useState<DrawerTab>('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // Reset state when drawer opens
  useEffect(() => {
    if (open) {
      setSearch('');
      setActiveTab('all');
    }
  }, [open]);

  const { data: rows = [] } = usePowerSyncQuery<PatientDrawerRow>(
    qPatientsDrawerList({
      userId,
      search: debouncedSearch,
      tab: activeTab,
    }),
  );

  const patients = useMemo(
    () =>
      rows.map((r) => ({
        id: r.id,
        name: r.full_name ?? 'Unknown',
        initials: getInitials(r.full_name ?? '?'),
        avatarColor: getPatientColor(r.id),
        photoUrl: r.photo_url ?? null,
        patientNo: r.patient_number ? `P-${r.patient_number}` : '',
        age: computeAge(r.birth_date),
        sex: r.sex ?? '',
        phone: r.phone ?? '',
        email: r.email ?? '',
        lastVisit: formatDate(r.last_visited_at ?? r.updated_at),
      })),
    [rows],
  );

  const handleSelectPatient = (p: (typeof patients)[number]) => {
    openPatientTab({
      id: p.id,
      label: getShortLabel(p.name),
      initials: getInitialsShort(p.name),
      color: getPatientColor(p.id),
      photoUrl: p.photoUrl,
    });
    navigate(`/patient/${p.id}`);
    onOpenChange(false);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Open Patient"
      width={440}
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div
          style={{
            padding: '0 var(--sp-2)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
          }}
        >
          {patients.length} patient{patients.length !== 1 ? 's' : ''} found
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Tabs */}
        <div
          style={{
            padding: '0 var(--sp-5) var(--sp-3)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--sp-2)',
          }}
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                style={{
                  height: 28,
                  padding: '0 var(--sp-3)',
                  borderRadius: 'var(--radius-full)',
                  border: isActive ? 'none' : '1px solid var(--border)',
                  background: isActive ? 'var(--gradient-primary)' : 'var(--bg-surface)',
                  color: isActive ? '#fff' : 'var(--text-muted)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div style={{ padding: '0 var(--sp-5) var(--sp-3)', position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              left: 32,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            type="text"
            placeholder="Search by name, patient #, phone, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 'var(--input-height, 40px)',
              padding: '0 var(--sp-4) 0 40px',
              borderRadius: 'var(--input-radius, 10px)',
              border: '1px solid var(--border-input)',
              background: 'var(--input-bg)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--input-text)',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Patient List */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          {patients.length === 0 ? (
            <div
              style={{
                padding: 'var(--sp-8)',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              No patients found
            </div>
          ) : (
            patients.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handleSelectPatient(p)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--sp-4)',
                  width: '100%',
                  padding: 'var(--sp-4) var(--sp-5)',
                  border: 'none',
                  borderBottom: '1px solid var(--border)',
                  background: 'var(--bg-surface)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  transition: 'background 100ms',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface-2)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface)';
                }}
              >
                {/* Avatar */}
                {p.photoUrl ? (
                  <img
                    src={p.photoUrl}
                    alt={p.name}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 'var(--radius-full)',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 'var(--radius-full)',
                      background: p.avatarColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-semi)',
                      color: '#fff',
                    }}
                  >
                    {p.initials}
                  </div>
                )}

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Name + Patient # */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-2)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-semi)',
                        color: 'var(--text)',
                        lineHeight: 'var(--line-height-sm)',
                      }}
                    >
                      {p.name}
                    </span>
                    {p.patientNo && (
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {p.patientNo}
                      </span>
                    )}
                  </div>

                  {/* Demographics */}
                  {(p.age !== null || p.sex) && (
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-muted)',
                        marginTop: 2,
                      }}
                    >
                      {[p.age !== null ? `${p.age}y` : '', p.sex ? capitalize(p.sex) : '']
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  )}

                  {/* Contact details */}
                  <div
                    style={{
                      marginTop: 6,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    {p.phone && (
                      <div style={contactRowStyle}>
                        <NavIcon name="phone" />
                        <span>{p.phone}</span>
                      </div>
                    )}
                    {p.email && (
                      <div style={contactRowStyle}>
                        <NavIcon name="mail" />
                        <span>{p.email}</span>
                      </div>
                    )}
                    {p.lastVisit && (
                      <div style={contactRowStyle}>
                        <NavIcon name="calendar" />
                        <span>Last visit: {p.lastVisit}</span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </Drawer>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

const contactRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
  lineHeight: '18px',
};
